/**
 * 產品圖片「下載到本機」的檔名規則（需求 R142）。
 *
 * PM 2026-08-29 拍板：
 *   Q4=A 副檔名依「圖片實際副檔名」，判斷不出來時退回 .jpg
 *   Q5=A 產品名稱含 Windows 檔名非法字元（反斜線 斜線 冒號 星號 問號 雙引號 小於 大於 直線）
 *        一律以底線「_」取代；名稱為空則用 product-image
 *
 * 為什麼要抽成獨立模組而不是寫在 ProductDetailPage 裡：
 *   1) AC16 要求 fixture 鎖住「檔名組成規則與非法字元取代」。ProductDetailPage.js 是
 *      一支近 1500 行、import 了 permissions/axios/元件的 React 頁面，fixture 只能對它
 *      做 AST／原始碼比對，無法真的呼叫函式驗算出「豬肉紙 → 豬肉紙.jpg」。抽出來之後
 *      fixture 可以直接 babel 載入本檔逐一驗算真實輸出。
 *   2) 本檔刻意**零 import**（不 import 任何同目錄模組），避免重演 需求 R140 的坑：
 *      前端 utils 新增相依會讓手動 babel 載入的 fixture 解析下游 './xxx' 失敗。
 *
 * 本檔不含任何 DOM／fetch 行為，純字串運算，故可在 Node 下直接驗算。
 */

/**
 * Windows 檔名非法字元（Q5=A 一律換成底線），外加 ASCII 控制字元 U+0000–U+001F
 * （同樣不能出現在檔名裡）。以 RegExp 建構式＋跳脫字串書寫，避免原始檔內出現裸控制字元。
 *
 * 刻意**不含**空白與連字號 —— 產品名稱本來就常有這兩者（例如「自然足跡-黑糖薑母茶」），
 * 它們是合法檔名字元，換成底線會違反 AC5「檔名即產品名稱」。
 */
// eslint-disable-next-line no-control-regex -- 控制字元正是這裡要濾掉的東西
const ILLEGAL_FILENAME_CHARS = new RegExp('[\\\\/:*?"<>|\\x00-\\x1f]', 'g');

/** 名稱為空（或整串都是非法字元被吃光）時的備援檔名（Q5=A）。 */
export const FALLBACK_IMAGE_BASENAME = 'product-image';

/** 判斷不出副檔名時的退場值（Q4=A）。 */
export const FALLBACK_IMAGE_EXTENSION = 'jpg';

/**
 * 只認得出來的圖片副檔名才採用。
 *
 * 為什麼要白名單：productImage 可能是舊資料的外部絕對網址（見
 * quotePdfExport.js:137 / catalogPdfExport.js:595 的 startsWith('http') 分支），
 * 這種網址的最後一段常常不是檔名，例如
 *   https://img.example.com/photo/12345      → 沒有點，本來就取不到
 *   https://example.com/i/abcd.php?id=9      → ".php" 不是圖片副檔名
 * 這兩種一律 fallback 成 .jpg（系統自家上傳的檔案 productImageStore 也一律轉存 .jpg）。
 */
const KNOWN_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'avif', 'heic', 'svg'];

/** MIME → 副檔名。URL 判斷不出來時的第二道來源（fetch 回來的 blob.type）。 */
const MIME_EXTENSION_MAP = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/pjpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/bmp': 'bmp',
  'image/x-ms-bmp': 'bmp',
  'image/avif': 'avif',
  'image/heic': 'heic',
  'image/svg+xml': 'svg',
};

/**
 * 從圖片來源解析副檔名（Q4=A）。
 *
 * 判斷順序：URL 路徑的副檔名 → blob 的 MIME type → 'jpg'。
 * URL 先於 MIME，是因為 AC4 要求「下載的位元組與 uploads 上的檔案一致」，
 * 檔名應該忠實反映伺服器上那個檔案本身，而不是瀏覽器推測的型別。
 *
 * @param {string} url productImage 值（站內相對路徑或 http 絕對網址皆可）
 * @param {string} [mimeType] 已下載 blob 的 type（可省略）
 * @returns {string} 不含點的副檔名，例如 'jpg'
 */
export function resolveImageExtension(url, mimeType) {
  const raw = typeof url === 'string' ? url : '';
  // 去掉 hash 與 query（#frag / ?v=2 都不屬於檔名），再取最後一段路徑。
  const pathname = raw.split('#')[0].split('?')[0];
  const lastSegment = pathname.split('/').pop() || '';
  const dotIndex = lastSegment.lastIndexOf('.');
  if (dotIndex > 0) {
    const ext = lastSegment.slice(dotIndex + 1).toLowerCase();
    if (KNOWN_IMAGE_EXTENSIONS.includes(ext)) return ext;
  }

  const mime = typeof mimeType === 'string' ? mimeType.split(';')[0].trim().toLowerCase() : '';
  if (MIME_EXTENSION_MAP[mime]) return MIME_EXTENSION_MAP[mime];

  return FALLBACK_IMAGE_EXTENSION;
}

/**
 * 把產品名稱洗成安全的檔名主檔名（Q5=A）。
 *
 * @param {string} productName Product.productName（中文產品名）
 * @returns {string} 安全主檔名；名稱為空／洗完是空的一律回 'product-image'
 */
export function sanitizeImageBaseName(productName) {
  const raw = typeof productName === 'string' ? productName : '';
  const replaced = raw.replace(ILLEGAL_FILENAME_CHARS, '_');
  // Windows 不允許檔名以點或空白結尾；前後空白也一併修掉，避免下載出「 豬肉紙 .jpg」。
  const trimmed = replaced.replace(/^[\s.]+/, '').replace(/[\s.]+$/, '');
  if (!trimmed) return FALLBACK_IMAGE_BASENAME;
  return trimmed;
}

/**
 * 組出下載檔名：產品名稱（中文）＋副檔名，不帶貨編、日期或任何前後綴（AC5）。
 *
 * @param {string} productName 產品名稱（中文）
 * @param {string} url productImage 值
 * @param {string} [mimeType] 已下載 blob 的 type（可省略）
 * @returns {string} 例如「豬肉紙.jpg」
 */
export function buildImageFileName(productName, url, mimeType) {
  return `${sanitizeImageBaseName(productName)}.${resolveImageExtension(url, mimeType)}`;
}

export default buildImageFileName;
